import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  UsersRound,
  UserPlus,
  Gamepad2,
  Bell,
  ShieldOff,
  Send,
  Search,
  X,
  MessageCircle,
  ChevronRight,
  Settings2,
} from 'lucide-react';
import { usePlatform } from '../store';
import { api, json } from '../api';
import { createPortal } from 'react-dom';
import { useGameOverlayPause } from './useGameOverlayPause';
import { UserAvatar } from './UserAvatar';
import './friends.css';
type Person = { id: string; username: string; name: string; avatarUrl: string; online: boolean };
type Invite = {
  id: string;
  roomId: string;
  gameId: string;
  gameName: string;
  from: Person;
  to: Person;
  incoming: boolean;
  expiresAt: number;
};
type Social = {
  friends: Person[];
  requests: { person: Person; incoming: boolean }[];
  invites: Invite[];
  blocked: Person[];
  allowInvites: boolean;
};
export function FriendsButton() {
  const { data } = usePlatform();
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const sync = (event: Event) => setExpanded(!!(event as CustomEvent).detail);
    window.addEventListener('moyu:friends-visibility', sync);
    return () => window.removeEventListener('moyu:friends-visibility', sync);
  }, []);
  if (!data?.user) return null;
  return (
    <button
      className="friends-trigger"
      title="好友与游戏邀请"
      aria-label="好友"
      data-friends-trigger
      aria-expanded={expanded}
      aria-controls={expanded ? 'friends-sidebar' : undefined}
      onClick={(event) =>
        window.dispatchEvent(
          new CustomEvent('moyu:open-friends', {
            detail: { toggle: true, trigger: event.currentTarget },
          }),
        )
      }
    >
      <UsersRound size={19} />
      <span>好友</span>
    </button>
  );
}
export function FriendsPanel() {
  const { data, notify } = usePlatform();
  const location = useLocation();
  const [open, setOpen] = useState(false),
    [social, setSocial] = useState<Social | null>(null),
    [tab, setTab] = useState<'friends' | 'requests' | 'inbox' | 'blocked'>('friends'),
    [username, setUsername] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [inviteRoom, setInviteRoom] = useState<{
      roomId?: string;
      partyId?: string;
      gameName: string;
    } | null>(null);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const panel = useRef<HTMLElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const [position, setPosition] = useState({ top: 72, height: 560 });
  useGameOverlayPause(open);
  const userId = data?.user?.id;
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('moyu:friends-visibility', { detail: open }));
  }, [open]);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const visual = window.visualViewport;
      const offset = visual?.offsetTop || 0;
      const bottom = offset + (visual?.height || innerHeight);
      const anchor = opener.current?.getBoundingClientRect();
      const top = Math.max(offset + 8, Math.min((anchor?.bottom || 64) + 12, bottom - 200));
      setPosition({ top, height: Math.max(120, bottom - top - 12) });
    };
    place();
    panel.current?.focus({ preventScroll: true });
    const outside = (event: PointerEvent) => {
      if (
        !panel.current?.contains(event.target as Node) &&
        !opener.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        opener.current?.focus({ preventScroll: true });
      }
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  const panelOpen = useRef(open);
  panelOpen.current = open;
  const refresh = async () => {
    const value = await api<Social>('/social');
    setSocial(value);
    return value;
  };
  useEffect(() => {
    setOpen(false);
    setSocial(null);
    setInviteRoom(null);
  }, [userId]);
  useEffect(() => {
    setInviteRoom(null);
    setOpen(false);
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (!userId) return;
    let disposed = false,
      pending = false;
    const read = async () => {
      if (document.hidden || pending || !panelOpen.current) return;
      pending = true;
      try {
        const value = await api<Social>('/social');
        if (!disposed) {
          setSocial(value);
        }
      } catch {
      } finally {
        pending = false;
      }
    };
    const show = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.toggle && panelOpen.current) {
        setOpen(false);
        return;
      }
      opener.current =
        detail?.trigger ||
        document.querySelector<HTMLElement>('[data-friends-trigger]') ||
        (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      setError('');
      setQuery('');
      setAdding(false);
      setInviteRoom(detail?.roomId || detail?.partyId ? detail : null);
      setTab('friends');
      panelOpen.current = true;
      setOpen(true);
      void read();
    };
    const hide = () => setOpen(false);
    window.addEventListener('moyu:open-notifications', hide);
    window.addEventListener('moyu:open-theme', hide);
    window.addEventListener('moyu:show-game-messages', hide);
    window.addEventListener('moyu:chat-opened', hide);
    window.addEventListener('moyu:social-changed', read);
    window.addEventListener('moyu:open-friends', show);
    window.addEventListener('focus', read);
    const timer = setInterval(read, 15000);
    void read();
    return () => {
      disposed = true;
      clearInterval(timer);
      window.removeEventListener('moyu:open-notifications', hide);
      window.removeEventListener('moyu:open-theme', hide);
      window.removeEventListener('moyu:show-game-messages', hide);
      window.removeEventListener('moyu:chat-opened', hide);
      window.removeEventListener('moyu:social-changed', read);
      window.removeEventListener('moyu:open-friends', show);
      window.removeEventListener('focus', read);
    };
  }, [userId]);
  const run = async (task: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await task();
      await refresh();
      window.dispatchEvent(new Event('moyu:notifications-refresh'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!userId || !open) return null;
  const person = (p: Person) => (
    <div className="friends-person">
      <UserAvatar src={p.avatarUrl} />
      <div>
        <strong>{p.name}</strong>
        <small>
          <span className={p.online ? 'friends-online' : ''}>{p.online ? '在线' : '离线'}</span>
          {' · @'}
          {p.username}
        </small>
      </div>
    </div>
  );
  const incoming = social?.requests.filter((r) => r.incoming) || [];
  const matches = (p: Person) =>
    `${p.name} ${p.username}`.toLowerCase().includes(query.trim().toLowerCase());
  const showMessages = () => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent('moyu:show-game-messages'));
  };
  return createPortal(
    <section
      id="friends-sidebar"
      ref={panel}
      tabIndex={-1}
      role="region"
      aria-label="好友列表"
      className="friends-panel friends-sidebar"
      style={{ top: position.top, maxHeight: position.height }}
    >
      <header className="friends-sidebar-header">
        <h2>好友</h2>
        <button onClick={() => setAdding(!adding)} aria-expanded={adding}>
          <UserPlus size={16} />
          添加
        </button>
        <button
          aria-label="关闭好友列表"
          onClick={() => {
            setOpen(false);
            opener.current?.focus({ preventScroll: true });
          }}
        >
          <X size={18} />
        </button>
      </header>
      <div className="friends-body">
        {inviteRoom && <p className="friends-room-note">邀请加入「{inviteRoom.gameName}」</p>}
        {adding && (
          <form
            className="friends-add"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await api('/social/requests', json('POST', { username }));
                setUsername('');
                notify('好友申请已发送');
              });
            }}
          >
            <input
              aria-label="好友账号"
              placeholder="输入好友的完整账号"
              value={username}
              maxLength={64}
              onChange={(e) => setUsername(e.target.value)}
            />
            <button className="button" disabled={busy || !username.trim()}>
              发送
            </button>
          </form>
        )}
        <label className="friends-search">
          <Search size={16} />
          <input
            aria-label="搜索好友"
            placeholder="搜索好友"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setTab('friends');
            }}
          />
        </label>
        <nav className="friends-tabs" aria-label="好友分类">
          <button className={tab === 'friends' ? 'active' : ''} onClick={() => setTab('friends')}>
            好友 <b>{social?.friends.length || 0}</b>
          </button>
          <button className={tab === 'requests' ? 'active' : ''} onClick={() => setTab('requests')}>
            申请 {incoming.length > 0 && <b>{incoming.length}</b>}
          </button>
          {(tab === 'inbox' || tab === 'blocked') && (
            <span className="friends-current-view">{tab === 'inbox' ? '已发送' : '屏蔽名单'}</span>
          )}
        </nav>
        {error && (
          <p className="friends-error" role="alert">
            {error}
          </p>
        )}
        {!social ? (
          <div className="friends-empty">正在打开好友列表…</div>
        ) : (
          <>
            {tab === 'friends' && (
              <>
                <div className="friends-list">
                  {social.friends.some(matches) ? (
                    [true, false].map((online) => (
                      <section className="friends-status-group" key={String(online)}>
                        {social.friends.some((p) => p.online === online && matches(p)) && (
                          <h3>
                            {online ? '在线' : '离线'} ·{' '}
                            {social.friends.filter((p) => p.online === online && matches(p)).length}
                          </h3>
                        )}
                        {social.friends
                          .filter((p) => p.online === online && matches(p))
                          .map((p) => (
                            <div className="friends-row" key={p.id}>
                              {person(p)}
                              <div className="friends-actions">
                                <button
                                  className="friends-chat-action"
                                  aria-label={`给${p.name}发私信`}
                                  title="发私信"
                                  onClick={() => {
                                    setOpen(false);
                                    window.dispatchEvent(
                                      new CustomEvent('moyu:open-chat', { detail: p.id }),
                                    );
                                  }}
                                >
                                  <MessageCircle size={20} />
                                </button>
                                {inviteRoom && (
                                  <button
                                    className="button"
                                    disabled={busy}
                                    onClick={() =>
                                      void run(async () => {
                                        await api(
                                          inviteRoom.partyId
                                            ? '/multiplayer/party/invites'
                                            : '/social/invites',
                                          json('POST', {
                                            ...(inviteRoom.partyId
                                              ? { partyId: inviteRoom.partyId }
                                              : { roomId: inviteRoom.roomId }),
                                            userId: p.id,
                                          }),
                                        );
                                        notify('游戏邀请已发送');
                                      })
                                    }
                                  >
                                    <Send size={15} />
                                    邀请
                                  </button>
                                )}
                                <details>
                                  <summary aria-label={`管理好友 ${p.name}`}>···</summary>
                                  <div>
                                    <button
                                      disabled={busy}
                                      onClick={() =>
                                        void run(() =>
                                          api(`/social/friends/${p.id}`, { method: 'DELETE' }),
                                        )
                                      }
                                    >
                                      移除好友
                                    </button>
                                    <button
                                      disabled={busy}
                                      onClick={() =>
                                        void run(() =>
                                          api(
                                            `/social/blocks/${p.id}`,
                                            json('POST', { block: true }),
                                          ),
                                        )
                                      }
                                    >
                                      屏蔽用户
                                    </button>
                                  </div>
                                </details>
                              </div>
                            </div>
                          ))}
                      </section>
                    ))
                  ) : (
                    <div className="friends-empty">
                      <UsersRound size={32} />
                      <strong>{query ? '没有找到好友' : '还没有岛上好友'}</strong>
                      <span>{query ? '换个名字或账号试试' : '点击右上角添加好友'}</span>
                    </div>
                  )}
                </div>
              </>
            )}
            {tab === 'requests' && (
              <div className="friends-list">
                {incoming.map((r) => (
                  <div className="friends-row" key={r.person.id}>
                    {person(r.person)}
                    <button className="friends-secondary" onClick={showMessages}>
                      查看申请
                    </button>
                  </div>
                ))}
                {!incoming.length && (
                  <div className="friends-empty">
                    <UserPlus size={28} />
                    <strong>暂无新的好友申请</strong>
                  </div>
                )}
                <button className="friends-footer-link" onClick={() => setTab('inbox')}>
                  已发送的申请与邀请 <ChevronRight size={16} />
                </button>
              </div>
            )}
            {tab === 'inbox' && (
              <div className="friends-list">
                {social.invites
                  .filter((i) => !i.incoming)
                  .map((i) => (
                    <div className="friends-invitation" key={i.id}>
                      <Gamepad2 size={24} />
                      <div>
                        <strong>{i.gameName}</strong>
                        <p>
                          {i.incoming
                            ? `${i.from?.name || '好友'} 邀请你一起玩`
                            : `已邀请 ${i.to?.name || '好友'}`}
                        </p>
                        <small>
                          邀请有效至{' '}
                          {new Date(i.expiresAt).toLocaleTimeString('zh-CN', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </small>
                      </div>

                      <button
                        disabled={busy}
                        className="friends-secondary"
                        onClick={() =>
                          void run(() => api(`/social/invites/${i.id}`, { method: 'DELETE' }))
                        }
                      >
                        撤回
                      </button>
                    </div>
                  ))}
                {social.requests
                  .filter((r) => !r.incoming)
                  .map((r) => (
                    <div className="friends-row" key={r.person.id}>
                      {person(r.person)}

                      <>
                        <small>等待回应</small>
                        <button
                          className="friends-secondary"
                          disabled={busy}
                          onClick={() =>
                            void run(() =>
                              api(`/social/friends/${r.person.id}`, { method: 'DELETE' }),
                            )
                          }
                        >
                          撤回
                        </button>
                      </>
                    </div>
                  ))}
                {!social.requests.some((r) => !r.incoming) &&
                  !social.invites.some((i) => !i.incoming) && (
                    <div className="friends-empty">
                      <Bell size={32} />
                      <strong>暂无待回应的申请或邀请</strong>
                      <span>收到的申请与邀请请到通知中心查看。</span>
                    </div>
                  )}
              </div>
            )}
            {tab === 'blocked' && (
              <div className="friends-list">
                {social.blocked.length ? (
                  social.blocked.map((p) => (
                    <div className="friends-row" key={p.id}>
                      {person(p)}
                      <button
                        disabled={busy}
                        className="friends-secondary"
                        onClick={() =>
                          void run(() =>
                            api(`/social/blocks/${p.id}`, json('POST', { block: false })),
                          )
                        }
                      >
                        解除屏蔽
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="friends-empty">
                    <ShieldOff size={32} />
                    <strong>没有屏蔽的用户</strong>
                  </div>
                )}
              </div>
            )}
            {tab === 'blocked' && (
              <label className="friends-preferences">
                <span>
                  <Bell size={16} />
                  接收好友的游戏邀请
                </span>
                <input
                  type="checkbox"
                  checked={social.allowInvites}
                  disabled={busy}
                  onChange={(e) =>
                    void run(() =>
                      api('/social/preferences', json('PATCH', { allowInvites: e.target.checked })),
                    )
                  }
                />
              </label>
            )}
          </>
        )}
      </div>
      <footer className="friends-sidebar-footer">
        <button onClick={showMessages}>
          <Gamepad2 size={18} />
          游戏邀请{' '}
          {social?.invites.filter((i) => i.incoming).length ? (
            <b>{social.invites.filter((i) => i.incoming).length}</b>
          ) : null}
          <ChevronRight size={16} />
        </button>
        <button
          aria-label="好友设置与屏蔽名单"
          title="好友设置"
          onClick={() => setTab(tab === 'blocked' ? 'friends' : 'blocked')}
        >
          <Settings2 size={18} />
        </button>
      </footer>
    </section>,
    document.body,
  );
}
